import React, { useRef, useState } from "react";
import { cn } from "~/lib/cn";
import { ICON_NAMES, type TIconName } from "~/lib/icon-names";
import { NamedIcon } from "./named-icon";

export interface IconPickerProps {
  value?: string;
  name?: string;
  id?: string;
  required?: boolean;
  disabled?: boolean;
  "aria-label"?: string;
  "aria-describedby"?: string;
  onChange?: (value: string) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

export const IconPicker = ({
  value,
  name,
  id,
  required,
  disabled,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedby,
  onChange,
  onBlur,
}: IconPickerProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  const filteredIcons = ICON_NAMES.filter((name) =>
    name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const selectedIcon =
    value && ICON_NAMES.includes(value as TIconName)
      ? (value as TIconName)
      : undefined;

  const handleIconSelect = (iconName: string) => {
    if (hiddenInputRef.current) {
      hiddenInputRef.current.value = iconName;
      const event = new Event("input", { bubbles: true });
      hiddenInputRef.current.dispatchEvent(event);
    }
    onChange?.(iconName);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <input
        type="hidden"
        ref={hiddenInputRef}
        name={name}
        id={id}
        value={value || ""}
        required={required}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedby}
        onBlur={onBlur}
      />
      <div className="flex items-center gap-4">
        <div className="flex grow-0">
          {selectedIcon && (
            <NamedIcon
              name={selectedIcon}
              className="text-brand-700 text-7xl size-[1em]"
            />
          )}
        </div>
        <div className="flex grow">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            disabled={disabled}
            className={cn(
              "input flex items-center gap-2",
              !selectedIcon && "text-gray-500",
            )}
          >
            {selectedIcon && <NamedIcon name={selectedIcon} />}
            <span>{selectedIcon || "Select an icon"}</span>
          </button>

          {isOpen && (
            <div className="absolute z-50 mt-1 w-full rounded-md border bg-white p-3 shadow-lg">
              <input
                type="text"
                name="search-icon"
                className={cn("input")}
                placeholder="Search icons..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />

              <div className="grid max-h-[280px] grid-cols-4 gap-2 overflow-y-auto">
                {filteredIcons.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => handleIconSelect(name)}
                    className={cn(
                      "flex flex-col items-center gap-1 rounded-md p-2 text-xs",
                      "transition-colors duration-200 hover:bg-purple-50",
                      value === name
                        ? "border-purple-300 bg-purple-100 text-purple-700"
                        : "text-text-tertiary border border-gray-200",
                    )}
                    title={name}
                  >
                    <NamedIcon name={name} className="text-2xl size-[1em]" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
